export const calculateCount = <T>(items: T[]): number => items.length;

export type UniqueCountOptions = {
  caseSensitive?: boolean;
  countEmptyStrings?: boolean;
  countNullishValues?: boolean;
  untrimmed?: boolean;
};
export const calculateUniqueCount = <ItemType, ValueType>(
  items: ItemType[],
  valueMapper: (item: ItemType) => ValueType,
  options: UniqueCountOptions = {},
): number =>
  items.reduce((memo, item) => {
    const { caseSensitive, countEmptyStrings, countNullishValues, untrimmed } = options;
    let normalizedValue = valueMapper(item);
    if (typeof normalizedValue === 'string') {
      normalizedValue = caseSensitive ? normalizedValue : (normalizedValue.toLowerCase() as ValueType);
      normalizedValue = untrimmed ? normalizedValue : ((normalizedValue as string).trim() as ValueType);
    }
    if (
      (!countEmptyStrings && normalizedValue === '') ||
      (!countNullishValues && (normalizedValue === undefined || normalizedValue === null))
    ) {
      return memo;
    }
    return memo.add(normalizedValue);
  }, new Set<ValueType>()).size;

export const calculateSum = <T>(items: T[], valueMapper: (item: T) => number): number =>
  items.reduce((memo, item) => memo + valueMapper(item), 0);

export const calculateAverage = <T>(items: T[], valueMapper: (item: T) => number): number => {
  if (!items.length) {
    return 0;
  }
  const total = calculateSum(items, valueMapper);
  return total / items.length;
};

export const calculatePercentageOfTotalCount = <T>(items: T[], predicate: (item: T) => boolean): number => {
  const percentageSum = items.filter((item) => predicate(item)).length;
  const totalSum = items.length;
  if (totalSum === 0) {
    return 0;
  }
  return (percentageSum / totalSum) * 100;
};
